import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateListValue,
  updateFieldValue,
  addItemToArray,
  updateObjectArrayValue,
  setCurrentTab,
  setEditInvestmentData,
  EditInvestmentState,
  clearEditInvestmentData,
  removeItemFromArray,
  handleNearbyLocations,
} from '../../../redux/project/editInvestmentSlice'; // Adjust the path if necessary
import { AppDispatch, RootState } from '../../../redux/store';
import ProjectForm from './InvestmentForm';
import Swal from 'sweetalert2';
import UpdateApi from '../../../api/genericApis/Update.api';
import { ModuleType } from '../../../api/modules.types';
import { useNavigate, useParams } from 'react-router-dom';
import GetByIdApi from '../../../api/genericApis/GetById.api';

const EditInvestment = () => {
  const dispatch: AppDispatch = useDispatch();
  const investmentData = useSelector(
    (state: RootState) => state.editInvestment,
  );
  const currentTab = investmentData.currentTab; // Get currentTab from Redux
  const totalTabs = 8; // Total number of steps (tabs)

  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState<boolean>(true); // Loading state

  const fetchData = async () => {
    if (!id) return;
    try {
      const data = (
        await GetByIdApi({ moduleType: ModuleType.investment, id: +id })
      ).data as unknown as EditInvestmentState;
      dispatch(setEditInvestmentData({ investmentData: data }));
      setLoading(false); // Data loaded, set loading to false
    } catch (error) {
      console.error('Error fetching data:', error);
      navigate('/investments');
    }
  };

  useEffect(() => {
    fetchData();
    return () => {
      dispatch(clearEditInvestmentData()); // Clean up on unmount
    };
  }, [id]);

  // Handle input changes for fields
  const navigate = useNavigate();

  const handleInputChange = ({
    e,
    section,
    field,
    lang,
    isSameValue,
    index,
  }: {
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    section: string;
    field: string;
    lang: 'ar' | 'en';
    isSameValue: boolean;
    index?: number;
  }) => {
    const { value } = e.target;

    if (index !== undefined) {
      dispatch(
        updateListValue({
          language: lang,
          section,
          field,
          index,
          value,
          isSameValue,
        }),
      );
    } else {
      dispatch(
        updateFieldValue({
          language: lang,
          section,
          field,
          value,
          isSameValue,
        }),
      );
    }
  };

  const handleObjectArrayInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    section: string,
    field: string,
    index: number,
    key: string,
    lang: 'ar' | 'en',
    isSameValue: boolean,
  ) => {
    const { value } = e.target;
    dispatch(
      updateObjectArrayValue({
        language: lang,
        section,
        field,
        index,
        key,
        value,
        isSameValue,
      }),
    );
  };

  const handleAddItem = (section: string, field: string, value?: any) => {
    dispatch(addItemToArray({ section, field, value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Confirm with SweetAlert before submitting
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to submit the updated Investment opportunity?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, submit!',
      cancelButtonText: 'No, cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        UpdateApi({
          moduleType: ModuleType.investment,
          id: Number(id),
          data: investmentData,
        })
          .then(() => {
            // On success
            Swal.fire({
              title: 'Success!',
              text: 'Investment opportunity has been successfully updated.',
              icon: 'success',
              confirmButtonText: 'Ok',
            });

            // Navigate to the service table page after success
            navigate('/investments');
          })
          .catch((error) => {
            // On error
            Swal.fire({
              title: 'Error!',
              text: 'There was an issue updating the Investment opportunity. Please try again.',
              icon: 'error',
              confirmButtonText: 'Ok',
            });
          });
      }
    });
  };

  // Handle tab change in Redux store
  const handleTabChange = (key: number) => {
    dispatch(setCurrentTab(key)); // Update the current tab in Redux
  };

  // Handle Next/Previous buttons
  const handleNext = () => {
    if (currentTab < totalTabs - 1) {
      dispatch(setCurrentTab(currentTab + 1)); // Move to next tab
    }
  };

  const handlePrevious = () => {
    if (currentTab > 0) {
      dispatch(setCurrentTab(currentTab - 1)); // Move to previous tab
    }
  };
  return (
    <ProjectForm
      currentTab={currentTab}
      totalTabs={totalTabs}
      investmentData={investmentData}
      handleInputChange={handleInputChange}
      handleAddItem={handleAddItem}
      handleObjectArrayInputChange={handleObjectArrayInputChange}
      handleTabChange={handleTabChange}
      handleNext={handleNext}
      handlePrevious={handlePrevious}
      handleSubmit={handleSubmit}
      removeItemFromArray={removeItemFromArray}
      handleNearbyLocations={handleNearbyLocations}
      title="Edit Investment Opportunity"
      flow="edit"
    />
  );
};

export default EditInvestment;
